import React, { useCallback, useState } from 'react';

import { errMsg } from '@/components/PageElement/ErrorMessage';
import { successMsg } from '@/components/PageElement/SuccessMessage';
import { createAddress } from 'api/user_profile/user_profile_create_address';
import {
  useMintWalletProfile,
  useMintWalletRaffle,
} from 'api/user_profile/user_profile_address_list';
import Modal from '@/components/PageElement/Modal';
import { useValidateAddr } from 'lib/useValidateAddr';
import { blockChainList } from 'lib/blockChain';
import { useAppDispatch } from 'store/hooks';
import { setRaffleMintWallet } from 'store/features/raffleDetailsSlice';
import { setMintWallet } from 'api/raffle/raffle_entry_mint_wallet';

interface IAddMintWalletProps {
  chain: string;
  openAddWallet: boolean;
  setCloseAddWallet: () => void;
  isRaffle?: boolean;
  isRegistered?: boolean;
  vanityUrl?: string;
}

const AddMintWallet: React.FC<IAddMintWalletProps> = ({
  chain,
  openAddWallet,
  setCloseAddWallet,
  isRaffle = false,
  isRegistered = false,
  vanityUrl = '',
}) => {
  const dispatch = useAppDispatch();
  const { getMintWalletProfile } = useMintWalletProfile();
  const { getMintWalletRaffle } = useMintWalletRaffle();
  const { isValidAddr } = useValidateAddr();
  const [inputAddress, setInputAddress] = useState('');
  const [isFocusAddress, setIsFocusAddress] = useState(false);
  const [inputWalletName, setInputWalletName] = useState('');
  const [isFocusName, setIsFocusName] = useState(false);

  const handleClose = useCallback(() => {
    setCloseAddWallet();
    setInputAddress('');
    setInputWalletName('');
  }, [setCloseAddWallet]);

  const handleConfirm = useCallback(async () => {
    if (!inputAddress) {
      errMsg('Address is required');
    } else if (!isValidAddr(inputAddress, chain)) {
      errMsg('Invalid address');
    } else {
      const res = await createAddress(
        chain,
        inputAddress.trim(),
        inputWalletName.trim()
      );
      if (res?.code === 101) {
        errMsg('Address has already added');
      } else if (res?.code === 0) {
        successMsg('Address saved');
        handleClose();
        if (isRaffle) {
          getMintWalletRaffle(chain);
          if (isRegistered) {
            const res1 = await setMintWallet(
              inputAddress.trim(),
              vanityUrl as string
            );
            if (res1?.code === 0) {
              successMsg('Mint Wallet Updated');
            } else {
              errMsg(res1?.msg);
            }
          }
          dispatch(setRaffleMintWallet(inputAddress.trim()));
        } else {
          getMintWalletProfile(chain);
        }
      } else {
        errMsg(res?.msg);
      }
    }
  }, [
    chain,
    dispatch,
    getMintWalletProfile,
    getMintWalletRaffle,
    handleClose,
    inputAddress,
    inputWalletName,
    isRaffle,
    isRegistered,
    isValidAddr,
    vanityUrl,
  ]);

  return (
    <Modal
      open={openAddWallet}
      handleCancel={handleClose}
      handleConfirm={handleConfirm}
      title={`Add ${
        blockChainList?.filter((item) => item?.chain === chain)[0]?.chainName
      } mint wallet`}
    >
      <div>
        <div className="relative mb-6 flex h-[72px] items-center gap-2 rounded-2xl border border-neutral-600">
          <input
            type="text"
            className="peer h-full w-full rounded-2xl border-none !bg-neutral-900 px-5 text-white placeholder:text-neutral-500 placeholder:peer-focus:hidden"
            placeholder={
              isFocusAddress
                ? ''
                : `+ ${
                    blockChainList?.filter((item) => item?.chain === chain)[0]
                      ?.chainName
                  } mint wallet`
            }
            onChange={(e) => {
              setInputAddress(e.target.value);
            }}
            value={inputAddress}
            onFocus={() => {
              setIsFocusAddress(true);
            }}
            onBlur={() => {
              setIsFocusAddress(false);
            }}
          />
          <div className="absolute -top-4 left-3 hidden bg-neutral-900 px-2 py-1 text-center text-sm font-normal  text-white peer-focus:block">
            {
              blockChainList?.filter((item) => item?.chain === chain)[0]
                ?.chainName
            }{' '}
            mint wallet
          </div>
        </div>

        <div className="relative mb-6 flex h-[72px] items-center gap-2 rounded-2xl border border-neutral-600">
          <input
            type="text"
            className="peer h-full w-full rounded-2xl border-none !bg-neutral-900 px-5 text-white placeholder:text-neutral-500 placeholder:peer-focus:hidden"
            placeholder={isFocusName ? '' : 'Wallet name'}
            onChange={(e) => {
              setInputWalletName(e.target.value);
            }}
            value={inputWalletName}
            onFocus={() => {
              setIsFocusName(true);
            }}
            onBlur={() => {
              setIsFocusName(false);
            }}
            maxLength={10}
          />
          <div className="absolute -top-4 left-3 hidden bg-neutral-900 px-2 py-1 text-center text-sm font-normal  text-white peer-focus:block">
            Wallet name
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddMintWallet;
